/**
 * Service to fetch residential mortgage industry data from the API.
 *
 * Contract:
 * GET /api/industry-data/residential-mortgage
 * Header: X-API-Key (case-insensitive)
 */

export type IndustryDatasetRow = Record<string, unknown>;

export interface IndustryDataset {
  columns: string[];
  rows: IndustryDatasetRow[];
}

export interface ResidentialMortgageIndustryData {
  metrics: string[];
  dateColumn: string; // e.g. "Reporting Date"
  datasets: Record<string, IndustryDataset>;
}

export interface ResidentialMortgageIndustryResponse {
  success: boolean;
  data: ResidentialMortgageIndustryData;
}

export class IndustryDataService {
  static readonly BASE_URL = "https://chain.crypsey.com";

  private static getResidentialMortgageEndpoint(): string {
    return `${this.BASE_URL}/api/industry-data/residential-mortgage`;
  }

  private static getApiKey(apiKey?: string): string {
    const key =
      apiKey?.trim() ||
      process.env.REACT_APP_INDUSTRY_DATA_API_KEY?.trim() ||
      process.env.REACT_APP_LOAN_STATISTICS_API_KEY?.trim();

    if (!key) {
      throw new Error("API key is required");
    }

    return key;
  }

  static async getResidentialMortgageIndustryData(
    apiKey?: string,
  ): Promise<ResidentialMortgageIndustryResponse> {
    const key = this.getApiKey(apiKey);

    const response = await fetch(this.getResidentialMortgageEndpoint(), {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": key,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          errorData.error ||
          `HTTP error! status: ${response.status}`,
      );
    }

    return (await response.json()) as ResidentialMortgageIndustryResponse;
  }
}
